import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// components
import { Layout, Loader } from "src/components";
import { FirstStepSection } from "./partials/FirstStepSection";
import { SecondStepSection } from "./partials/SecondStepSection";
import { ThirdStepSection } from "./partials/ThirdStepSection/ThirdStepSection";
import PublishSuccessModal from "./partials/PublishSuccessModal";

// utils
import { getAssignmentsByIdActions, publishAssignmentActions } from "src/redux/actions/assignments";
import { RootReducer } from "src/redux/reducers";
import { getUserInfoActions } from "src/redux/actions/user";
import { useTranslation } from "react-i18next";
import useSkinPath from "src/hooks/useSkinPath";
import useSchools from "../../hooks/useSchools";
import { EDIT_ASSIGNMENT_QUICK_MODE } from "../../constants";

interface IProps {
  isEditing?: boolean;
}

const CreateAssignment: FC<IProps> = ({ isEditing }) => {
  const { path } = useSkinPath();
  const { id, editMode } = useParams();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get("duplicate");
  const location = useLocation();
  const preselectedBook = (
    location.state as {
      preselectedBook?: { id: number; title: string; thumbnailUrl?: string; contentTypes?: string[] };
    } | null
  )?.preselectedBook;
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showPublishSuccess, setShowPublishSuccess] = useState(false);

  const {
    assignments: { assignments, isLoading: isAssignmentsLoading },
    group: { isLoading: isGroupLoading, groups },
    auth: {
      authData: { userId }
    },
    user: { userData }
  }: RootReducer = useSelector<RootReducer>((state) => state) as RootReducer;

  const isLoading = useMemo(() => isAssignmentsLoading || isGroupLoading, [isAssignmentsLoading, isGroupLoading]);

  const { activeSchoolId, getGroups } = useSchools();

  const getUserInfo = useCallback(() => dispatch(getUserInfoActions.request({ userId: userId || "" })), [dispatch]);

  const getAssignmentById = useCallback(() => {
    if (id) {
      return dispatch(
        getAssignmentsByIdActions.request({
          id: Number(id),
          subscriptionId: userData?.subscriptionId
        })
      );
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (isEditing) {
      getAssignmentById();
    }
    if (duplicateId) {
      dispatch(
        getAssignmentsByIdActions.request({
          id: Number(duplicateId),
          subscriptionId: userData?.subscriptionId
        })
      );
    }
    getUserInfo();
    activeSchoolId && getGroups();
  }, [isEditing, activeSchoolId, duplicateId]);

  const assignment = useMemo(() => assignments?.find((item) => item.assignmentId === Number(id)), [id, assignments]);

  const duplicateSource = useMemo(
    () => (duplicateId ? assignments?.find((item) => item.assignmentId === Number(duplicateId)) : undefined),
    [duplicateId, assignments]
  );

  const secondStepRef = useRef<HTMLDivElement | null>(null);

  const scrollToSecondStep = () => {
    if (secondStepRef.current) {
      secondStepRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (id && assignment?.assignmentId && assignment.content?.content?.length === 0) {
      scrollToSecondStep();
    }
  }, [id, assignment]);

  const handlePublish = useCallback(() => {
    if (assignment?.assignees.length && assignment?.content.content.length) {
      dispatch(
        publishAssignmentActions.request({
          id: Number(id),
          onSuccess: () => setShowPublishSuccess(true)
        })
      );
    }
  }, [dispatch, id, assignment]);

  return (
    <Layout>
      {isLoading && <Loader />}
      <div className="create-assignment-page">
        <div className="main_breadcrumb">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item" aria-current="page">
                <Link to={path("/assignments")}>{t("menu_item_assignments")}</Link>
              </li>
              <li className="breadcrumb-item">{t("assignment_create_btn")}</li>
            </ol>
          </nav>
        </div>
        <FirstStepSection
          editMode={editMode}
          assignment={duplicateSource ?? assignment}
          preselectedBook={preselectedBook}
        />
        {id && editMode !== EDIT_ASSIGNMENT_QUICK_MODE && (
          <div ref={secondStepRef}>
            <SecondStepSection assignment={assignment} />
            <ThirdStepSection assignment={assignment} groups={groups?.rows} handlePublish={handlePublish} />
          </div>
        )}
      </div>
      {showPublishSuccess && id && (
        <PublishSuccessModal
          assignmentId={Number(id)}
          onViewAssignment={() => navigate(path(`/assignments/${id}`))}
          onCreateAnother={() => {
            setShowPublishSuccess(false);
            navigate(path("/assignments/create"));
          }}
        />
      )}
    </Layout>
  );
};

export default CreateAssignment;
